"use client";

import { useSession } from "@/components/SupabaseProvider";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import {
  discardUnownedSocialProviderAccounts,
  readSocialProviderAccountsCache,
  removeSocialProviderAccountsCache,
  writeSocialProviderAccountsCache,
} from "../helpers/socialProviderAccountsCache";
import { SocialProviderType } from "../types/models";
import { snakeToCamelCase } from "../utils/api";
import { useDemoMode } from "./demoModeContext";

export const openOAuthWindow = (url: string, signal?: AbortSignal, popup?: Window) => {
  if (signal?.aborted) return;
  if (popup) {
    if (popup.closed) throw new Error("The connection window was closed. Please try again.");
    popup.location.replace(url);
    return;
  }
  const timer = setTimeout(() => {
    signal?.removeEventListener("abort", cancel);
    if (!signal?.aborted) window.open(url, "_blank"); // Safari trickshot
  });
  const cancel = () => clearTimeout(timer);
  signal?.addEventListener("abort", cancel, { once: true });
};

export interface SocialProviderAccount {
  id: string;
  provider: SocialProviderType;
  name: string;
  username: string;
  profilePicture: string;
  settings?: Record<string, unknown>;
  /**
   * Set by the API when the provider rejected the token's refresh grant
   * (revoked, expired beyond refresh, ...). Only re-running the provider's
   * OAuth flow (`authorize(provider, account.id)`) fixes it; the token row is updated in
   * place, so the account keeps its id and settings.
   */
  needsReconnect?: boolean;
  /** This connection can only publish public YouTube videos. */
  publicOnly?: boolean;
  /** Public YouTube posts use the confirmed PostBridge connection. */
  postbridgeConnected?: boolean;
  /** A never-paid connection's activation window was reclaimed. */
  activationExpired?: boolean;
}

/**
 * The integrations/accounts API uses id-only placeholders when provider profile
 * fetch fails or times out, and flags `needsReconnect` when the token is known
 * to be dead. Neither is suitable for publishing.
 *
 * Every code path that gates the publishing flow on "does the user have at
 * least one social account?" MUST filter with this predicate. Checking raw
 * length is unsafe: a placeholder-only user would be routed into the publish
 * dialog, which in turn filters them out and bounces back to the connect
 * dialog — creating an infinite loop (see `hasAnyAuthenticatedSocialProviderAccount`).
 */
export function isAuthenticatedSocialProviderAccount(
  account: SocialProviderAccount,
): boolean {
  if (account.needsReconnect) return false;
  return !(
    account.name === account.id && account.username === account.id
  );
}

/**
 * The account's handle for display: the username, falling back to the name,
 * with any leading "@" stripped. YouTube stores channel handles as "@name",
 * the other providers store them bare, so callers that print a handle go
 * through here rather than reading `username` directly.
 */
export function getSocialAccountHandle(
  account: Pick<SocialProviderAccount, "username" | "name">,
): string {
  return (account.username || account.name || "").replace(/^@+/, "");
}

const SOCIAL_PROVIDER_NAMES: Record<SocialProviderType, string> = {
  tiktok: "TikTok",
  youtube: "YouTube",
  linkedin: "LinkedIn",
  instagram: "Instagram",
  facebook: "Facebook",
};

const profileLabel = (value: unknown, accountId: string): string => {
  const label = typeof value === "string" ? value.trim() : "";
  return label.replace(/^@+/, "") === accountId ? "" : label;
};

/** Keep the API's id-only placeholder out of user-facing account labels. */
export function getSocialAccountDisplayName(
  account: Pick<SocialProviderAccount, "id" | "provider" | "username" | "name">,
): string {
  return profileLabel(account.username, account.id)
    || profileLabel(account.name, account.id)
    || SOCIAL_PROVIDER_NAMES[account.provider];
}

// A legacy expired token may have no profile saved on the server. Keep the
// identity this browser already knows, while retaining the server's current
// reconnect flag and capabilities. Never hydrate a publishing placeholder:
// isAuthenticatedSocialProviderAccount relies on its id-only fields.
const retainReconnectProfile = (
  account: SocialProviderAccount,
  knownAccounts: readonly SocialProviderAccount[],
): SocialProviderAccount => {
  if (!account.needsReconnect
    || profileLabel(account.username, account.id)
    || profileLabel(account.name, account.id)) return account;
  const known = knownAccounts.find((candidate) =>
    candidate.id === account.id && candidate.provider === account.provider
    && (profileLabel(candidate.username, candidate.id) || profileLabel(candidate.name, candidate.id)),
  );
  if (!known) return account;
  return {
    ...account,
    name: known.name,
    username: known.username,
    profilePicture: account.profilePicture || known.profilePicture,
  };
};

/**
 * Returns true iff the list contains at least one non-placeholder account.
 * Prefer this over `accounts.length > 0` whenever the answer is used to decide
 * whether the user can publish / whether to skip the connect-accounts flow.
 */
export function hasAnyAuthenticatedSocialProviderAccount(
  accounts: readonly SocialProviderAccount[] | null | undefined,
): boolean {
  return Array.isArray(accounts) && accounts.some(isAuthenticatedSocialProviderAccount);
}

// Successful reads of the remote account list on this page. The scheduler's
// INIT waits for this count to move before acting on a freshly verified list.
let remoteAccountReads = 0;
export const countRemoteAccountReads = () => remoteAccountReads;

export const useSocialProviderAccounts = ({ freshOnMount = false }: { freshOnMount?: boolean } = {}) => {
  const session = useSession();
  const userId = session?.user.id;
  const queryClient = useQueryClient();
  useEffect(discardUnownedSocialProviderAccounts, []);

  const authorize = async (provider: SocialProviderType, accountId?: string, options?: { via: "postbridge"; signal?: AbortSignal; popup?: Window }) => {
    const closePendingPopup = () => options?.popup?.close();
    options?.signal?.addEventListener("abort", closePendingPopup, { once: true });
    try {
      options?.signal?.throwIfAborted();

      const params = new URLSearchParams();
      if (accountId) params.set("account_id", accountId);
      if (options?.via) params.set("via", options.via);
      const query = params.size ? `?${params}` : "";
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_API_URL}/integrations/providers/${provider}/authorize${query}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${session?.access_token}`,
          },
          ...(options?.signal ? { signal: options.signal } : {}),
        }
      );
      const resJson = snakeToCamelCase(await response.json().catch(() => null));
      options?.signal?.throwIfAborted();
      if (!response.ok)
        throw new Error(
          typeof resJson?.message === "string"
            ? resJson.message
            : "Couldn't start the connection. Please try again.",
        );

      if (typeof resJson?.url !== "string" || !resJson.url)
        throw new Error("Couldn't start the connection. Please try again.");

      openOAuthWindow(resJson.url, options?.signal, options?.popup);
    } catch (error) {
      closePendingPopup();
      if (!options?.signal?.aborted) console.error(error);
      throw error;
    } finally {
      options?.signal?.removeEventListener("abort", closePendingPopup);
    }
  };

  const deleteAccountById = async ({
    provider,
    id,
  }: {
    provider: string;
    id: string;
  }) => {
    if (!userId || !session?.access_token) throw new Error("Not signed in");
    try {
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_API_URL}/integrations/providers/${provider}/accounts/${id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${session?.access_token}`,
          },
        }
      );
      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(
          typeof body?.message === "string"
            ? body.message
            : "Couldn't disconnect the account. Please try again.",
        );
      }

      const accounts = readSocialProviderAccountsCache(userId);
      if (accounts) {
        writeSocialProviderAccountsCache(
          userId,
          accounts.filter((account) => account.id !== id),
        );
      }
      return userId;
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  const deleteMutation = useMutation({
    mutationKey: ["deleteSocialProviderAccountById"],
    mutationFn: deleteAccountById,
    onSuccess: (requestUserId) => {
      queryClient.invalidateQueries({
        queryKey: ["socialProviderAccounts", requestUserId],
      });
    },
  });

  const loadRemoteAccounts = async (signal: AbortSignal) => {
    if (!userId || !session?.access_token) return [];
    signal.throwIfAborted();
    const response = await fetch(
      `${process.env.NEXT_PUBLIC_API_URL}/integrations/accounts`,
      {
        headers: { authorization: `Bearer ${session.access_token}` },
        signal,
      },
    );
    // A previous login's request may finish after the observer changed owner.
    // Never let that result revive a cache cleared by logout/cancellation.
    signal.throwIfAborted();
    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        removeSocialProviderAccountsCache(userId);
      }
      throw new Error(`Failed to fetch accounts (${response.status})`);
    }
    const responseJson = await response.json();
    signal.throwIfAborted();
    const knownAccounts = [
      ...(queryClient.getQueryData<SocialProviderAccount[]>(["socialProviderAccounts", userId]) ?? []),
      ...(readSocialProviderAccountsCache(userId) ?? []),
    ];
    const accounts = Array.isArray(responseJson)
      ? responseJson.map((account) => retainReconnectProfile(snakeToCamelCase(account), knownAccounts))
      : [];
    remoteAccountReads += 1;
    writeSocialProviderAccountsCache(userId, accounts);
    return accounts;
  };

  const readAccounts = useQuery<SocialProviderAccount[]>({
    queryKey: ["socialProviderAccounts", userId],
    queryFn: ({ signal }) => loadRemoteAccounts(signal),
    enabled: Boolean(userId && session?.access_token),
    // Ignore QueryObserver's previous data on an account switch. Persisted
    // data is display-only: every cold query must verify it with the server.
    placeholderData: () =>
      session?.access_token ? readSocialProviderAccountsCache(userId) : undefined,
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: "always",
    // A mounted scheduler must also refresh any warm in-memory query data.
    ...(freshOnMount ? {
      refetchOnMount: "always" as const,
      // Connection reclamation can happen while the scheduler stays focused.
      // Profiles are cached by the API, so this only rechecks credential state.
      refetchInterval: 60_000,
    } : {}),
  });

  // Demo mode's calendar and analytics show its posts' accounts.
  const demoAccounts = useDemoMode()?.accounts;

  return {
    authorize,
    disconnect: deleteMutation,
    readAccounts: demoAccounts ? { ...readAccounts, data: demoAccounts } : readAccounts,
  };
};
